import React, { useEffect, useState } from "react";
import api from "../api/api";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Line, Pie } from "react-chartjs-2";
import { format, subDays, isWithinInterval } from "date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Utility to generate random colors dynamically
const generateColors = (count) => {
  const colors = [];
  for (let i = 0; i < count; i++) {
    const r = Math.floor(Math.random() * 200 + 55);
    const g = Math.floor(Math.random() * 200 + 55);
    const b = Math.floor(Math.random() * 200 + 55);
    colors.push(`rgba(${r}, ${g}, ${b}, 0.7)`);
  }
  return colors;
};

const AnalyticsDashboard = () => {
  const [interviews, setInterviews] = useState([]);
  const [roundsData, setRoundsData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch interviews & rounds (backend filters last-30-days & per-user)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const interviewsRes = await api.get("/interviews");
        const roundsRes = await api.get("/analytics/rounds-per-interview");

        const allInterviews = interviewsRes.data?.interviews || [];
        const last30DaysInterviews = allInterviews.filter(
          (i) =>
            i.date &&
            isWithinInterval(new Date(i.date), {
              start: subDays(new Date(), 30),
              end: new Date(),
            })
        );

        setInterviews(last30DaysInterviews);
        setRoundsData(roundsRes.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch analytics data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Last 30 days labels
  const last30DaysLabels = Array.from({ length: 30 }, (_, i) =>
    format(subDays(new Date(), 29 - i), "MMM d")
  );

  // Interviews per day
  const interviewCounts = last30DaysLabels.map((day) =>
    interviews.filter(
      (i) => i.date && format(new Date(i.date), "MMM d") === day
    ).length
  );

  // Time per interview
  const timePerInterview = interviews.map((i) => i.duration || 0);
  const timeLabels = interviews.map((i) => i.position || "Unknown");

  // Top companies
  const companyCounts = {};
  interviews.forEach((i) => {
    if (i.company) companyCounts[i.company] = (companyCounts[i.company] || 0) + 1;
  });
  const topCompanies = Object.keys(companyCounts);
  const topCompanyData = Object.values(companyCounts);
  const topCompanyColors = generateColors(topCompanies.length);

  // Avg rounds per role grouped by company
  const roundsByCompanyRole = {};
  roundsData.forEach((r) => {
    const interviewDate = r.date ? new Date(r.date) : null;
    if (!interviewDate || !isWithinInterval(interviewDate, { start: subDays(new Date(), 30), end: new Date() })) return;

    const key = `${r.company || "Unknown"} - ${r.position || "Unknown"}`;
    roundsByCompanyRole[key] = roundsByCompanyRole[key] || [];
    roundsByCompanyRole[key].push(r.roundCount || 0);
  });

  const avgRoundsLabels = Object.keys(roundsByCompanyRole);
  const avgRoundsData = Object.values(roundsByCompanyRole).map(
    (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0)
  );

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-blue-50 to-purple-100">
      <h1 className="text-3xl font-bold mb-6 text-center">📊 Analytics Dashboard (Last 30 Days)</h1>

      {loading ? (
        <p className="text-center text-lg font-medium">Loading analytics...</p>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-4 rounded-2xl shadow text-center">
              <h2 className="text-lg font-semibold mb-2">Total Interviews</h2>
              <p className="text-2xl font-bold">{interviews.length}</p>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow text-center">
              <h2 className="text-lg font-semibold mb-2">Unique Companies</h2>
              <p className="text-2xl font-bold">{topCompanies.length}</p>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow text-center">
              <h2 className="text-lg font-semibold mb-2">Avg Duration (mins)</h2>
              <p className="text-2xl font-bold">
                {timePerInterview.length
                  ? (timePerInterview.reduce((a, b) => a + b, 0) / timePerInterview.length).toFixed(1)
                  : 0}
              </p>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow text-center">
              <h2 className="text-lg font-semibold mb-2">Avg Rounds / Role</h2>
              <p className="text-2xl font-bold">
                {avgRoundsData.length
                  ? (avgRoundsData.reduce((a, b) => a + b, 0) / avgRoundsData.length).toFixed(1)
                  : 0}
              </p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Bar: Interviews Over Last 30 Days */}
            <div className="bg-white p-4 rounded-2xl shadow">
              <h2 className="text-xl font-semibold mb-2">📅 Interviews - Last 30 Days</h2>
              <Bar
                data={{
                  labels: last30DaysLabels,
                  datasets: [
                    {
                      label: "Interviews",
                      data: interviewCounts,
                      backgroundColor: "rgba(75, 192, 192, 0.6)",
                    },
                  ],
                }}
                options={{ responsive: true, plugins: { legend: { display: false } } }}
              />
            </div>

            {/* Line: Time per Interview */}
            <div className="bg-white p-4 rounded-2xl shadow">
              <h2 className="text-xl font-semibold mb-2">⏱️ Time per Interview (minutes)</h2>
              <Line
                data={{
                  labels: timeLabels,
                  datasets: [
                    {
                      label: "Duration",
                      data: timePerInterview,
                      borderColor: "rgba(153, 102, 255, 1)",
                      backgroundColor: "rgba(153, 102, 255, 0.2)",
                    },
                  ],
                }}
                options={{ responsive: true, plugins: { legend: { position: "top" } } }}
              />
            </div>

            {/* Pie: Top Companies */}
            <div className="bg-white p-4 rounded-2xl shadow">
              <h2 className="text-xl font-semibold mb-2">🏢 Top Companies</h2>
              <Pie
                data={{
                  labels: topCompanies,
                  datasets: [
                    {
                      label: "# Interviews",
                      data: topCompanyData,
                      backgroundColor: topCompanyColors,
                    },
                  ],
                }}
                options={{ responsive: true }}
              />
            </div>

            {/* Bar: Avg Rounds per Role (grouped) */}
            <div className="bg-white p-4 rounded-2xl shadow">
              <h2 className="text-xl font-semibold mb-2">🔄 Avg Rounds per Role</h2>
              <Bar
                data={{
                  labels: avgRoundsLabels,
                  datasets: [
                    {
                      label: "Average Rounds",
                      data: avgRoundsData.map((v) => parseFloat(v.toFixed(1))),
                      backgroundColor: "rgba(255, 159, 64, 0.6)",
                      borderRadius: 8,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: (tooltipItem) => `Avg: ${tooltipItem.raw}`,
                      },
                    },
                  },
                  scales: {
                    x: {
                      title: { display: true, text: "Company - Role" },
                      ticks: { autoSkip: false, maxRotation: 90, minRotation: 45 },
                    },
                    y: { title: { display: true, text: "Rounds" }, beginAtZero: true },
                  },
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
